import type { EntityDef, EntitySnapshot, PlayerIndex } from '@md/shared';
import type { ParsedLevel } from '../level/parser.js';
import type { PlayerRuntime } from '../module/player.js';
import type { Aabb } from '../physics/aabb.js';

/**
 * Entités du moteur (boutons, portes, ascenseurs, collectibles… et tout ce
 * qu'un GameModule ajoute).
 *
 * Contrainte structurante : `tick()` est PUR (spec §3). Une entité est donc un
 * objet de DONNÉES clonable par copie superficielle. Son comportement est une
 * fonction `update(self, ctx)` **sans état** — la configuration issue du JSON
 * est capturée par fermeture au moment de la fabrication, jamais mutée. Le
 * clone conserve la même référence de fonction : rien à sérialiser, rien à
 * ré-instancier.
 */

/** Forme de rendu, volontairement générique (le client ignore les jeux). */
export type EntityShape = 'box' | 'plate' | 'bar' | 'gem' | 'ring' | 'lever';

export interface EntityAppearance {
  readonly shape: EntityShape;
  /** Couleur au repos, 0xRRGGBB. */
  readonly tint: number;
  /** Couleur quand `flags.active` vaut 1 (sinon identique à `tint`). */
  readonly activeTint: number;
}

/**
 * Ordre d'exécution des entités dans le tick, du plus petit au plus grand :
 *
 *  1. `BODY` — objets physiques (blocs) : ils bougent d'abord ;
 *  2. `SENSOR` — boutons, leviers, collectibles, cycles d'électroaimant :
 *     ils voient donc les objets à leur position définitive ;
 *  3. `ACTUATOR` — portes, ascenseurs : ils lisent les signaux fraîchement
 *     publiés, donc un bouton pressé ouvre sa porte DANS LE MÊME tick.
 */
export const ENTITY_PHASE_BODY = 0;
export const ENTITY_PHASE_SENSOR = 1;
export const ENTITY_PHASE_ACTUATOR = 2;

export interface EntityInstance {
  readonly id: string;
  readonly type: string;
  /** Coin haut-gauche de la hitbox, en tuiles. */
  x: number;
  y: number;
  readonly w: number;
  readonly h: number;
  /** Vitesse, en tuiles/s (blocs, ascenseurs). */
  vx: number;
  vy: number;
  /** Bloque les corps dans les quatre directions sur ce tick. */
  solid: boolean;
  /** Ne bloque que par le dessus (plateforme mobile fine). */
  oneWay: boolean;
  readonly phase: number;
  /** État observable, toujours numérique donc sérialisable tel quel. */
  flags: Record<string, number>;
  readonly appearance: EntityAppearance;
  /** Comportement sans état, appelé à l'étape « entités » du tick. */
  readonly update: ((self: EntityInstance, ctx: EntityCtx) => void) | null;
}

/** Contexte fourni à `EntityInstance.update`. */
export interface EntityCtx {
  readonly tick: number;
  readonly dt: number;
  readonly level: ParsedLevel;
  readonly players: readonly [PlayerRuntime, PlayerRuntime];
  readonly entities: readonly EntityInstance[];
  /** Signal publié par l'entité d'identifiant `id` (`flags.active === 1`). */
  signal(id: string): boolean;
  /** Entité d'identifiant `id`, ou `undefined`. */
  entity(id: string): EntityInstance | undefined;
}

export type EntityFactory = (def: EntityDef) => EntityInstance;

/** Options de fabrication d'une entité ; tout le reste a une valeur par défaut. */
export interface CreateEntityOptions {
  readonly id: string;
  readonly type: string;
  readonly x: number;
  readonly y: number;
  readonly w?: number;
  readonly h?: number;
  readonly solid?: boolean;
  readonly oneWay?: boolean;
  readonly phase?: number;
  readonly flags?: Readonly<Record<string, number>>;
  readonly appearance: EntityAppearance;
  readonly update?: (self: EntityInstance, ctx: EntityCtx) => void;
}

export function createEntity(options: CreateEntityOptions): EntityInstance {
  return {
    id: options.id,
    type: options.type,
    x: options.x,
    y: options.y,
    w: options.w ?? 1,
    h: options.h ?? 1,
    vx: 0,
    vy: 0,
    solid: options.solid ?? false,
    oneWay: options.oneWay ?? false,
    phase: options.phase ?? ENTITY_PHASE_SENSOR,
    flags: { ...options.flags },
    appearance: options.appearance,
    update: options.update ?? null,
  };
}

/** Copie superficielle + copie des drapeaux : suffit à garder `tick()` pur. */
export function cloneEntity(entity: EntityInstance): EntityInstance {
  return { ...entity, flags: { ...entity.flags } };
}

export function entityAabb(entity: EntityInstance): Aabb {
  return { x: entity.x, y: entity.y, w: entity.w, h: entity.h };
}

export function entitySnapshot(entity: EntityInstance): EntitySnapshot {
  return {
    id: entity.id,
    type: entity.type,
    x: entity.x,
    y: entity.y,
    w: entity.w,
    h: entity.h,
    vx: entity.vx,
    vy: entity.vy,
    solid: entity.solid,
    flags: { ...entity.flags },
  };
}

/**
 * Identifiant par défaut d'une entité qui n'en déclare pas : dérivé de son type
 * et de sa position, donc STABLE d'une partie à l'autre (déterminisme) et
 * unique tant que deux entités du même type ne se superposent pas.
 */
export function defaultEntityId(type: string, x: number, y: number): string {
  return `${type}@${x},${y}`;
}

/** Le joueur d'index `index` est-il autorisé par la contrainte `player` ? */
export function matchesPlayer(index: PlayerIndex, allowed: PlayerIndex | undefined): boolean {
  return allowed === undefined || allowed === index;
}
